import { getCompanyContext } from "@api/core-api-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import { SdbCompany } from "@custom-types/sdb-company-types";
import { IReactChildrenOnly } from "@custom-types/types";
import { isLocalhost } from "@faro-lotv/foundation";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@hooks/use-toast";
import { useTrackPageLoad } from "@hooks/use-track-page-load";
import { useTrackTokenMetrics } from "@hooks/use-track-token-metrics";
import { useWorkspaceParams } from "@hooks/workspaces/use-workspace-params";
import { ForbiddenAccessPage } from "@pages/workspace/forbidden-access-page";
import { SelectWorkspacePage } from "@pages/workspace/select-workspace-page";
import { AuthenticatedRoute } from "@router/authenticated-route";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";
import { appStateSelector } from "@store/app/app-selector";
import { setSelectedProjectId } from "@store/projects/projects-slice";
import {
  sdbCompaniesSelector,
  selectedSdbCompanyIdSelector,
  isFeatureAvailableInCompanySelector,
  fetchingSdbCompanyFlagsSelector,
  availableFeaturesInCompanySelector,
} from "@store/sdb-company/sdb-company-selector";
import { setSelectedSdbCompanyId } from "@store/sdb-company/sdb-company-slice";
import {
  fetchCompaniesAndWorkspaces,
  fetchCompanyCommunicationSettings,
  fetchCompanyContext,
  fetchCompanySubscriptions,
} from "@store/sdb-company/sdb-company-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SubscriptionExpiredEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hooks that loads sdbCompanies (workspaces/companies) and redirect to main app page from root
 */
export function SdbCompanyLoader({
  children,
}: IReactChildrenOnly): JSX.Element {
  useTrackPageLoad();
  useTrackTokenMetrics();
  const { trackEvent } = useTrackEvent();
  const appParams = useAppParams();

  const selectedSdbCompanyId = useAppSelector(selectedSdbCompanyIdSelector);
  const sdbCompanies = useAppSelector(sdbCompaniesSelector);
  const { isDevModeEnabled, isBetaTestingEnabled } =
    useAppSelector(appStateSelector);
  const availableFeaturesInCompany = useAppSelector(
    availableFeaturesInCompanySelector
  );
  const isCoreProductFeatureEnabled = useAppSelector(
    isFeatureAvailableInCompanySelector("core-product")
  );
  const hasTrackedSubscriptionExpiredEvent = useRef<boolean>(false);

  /**
   * Tracks the last workspace ID for which we showed an access denied toast.
   * Prevents duplicate toasts in React Strict Mode and across re-renders.
   */
  const lastToastShownForWorkspaceId = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const { navigateToProjects, navigateToSubscriptionExpiredPage } =
    useAppNavigation();
  const { setLoadingSpinner, isLoadingSpinnerShowing } = useLoadingSpinner();
  const { showToast } = useToast();
  useWorkspaceParams();
  const { canViewSubscriptionDetails } = useHasUserValidRoleCompanyLevel();

  const [shouldShowSelectWorkspaceMenu, setShouldShowSelectWorkspaceMenu] =
    useState<boolean>(false);

  /** Show the no workspace access page when user has no workspaces and was denied access */
  const [shouldShowNoWorkspaceAccessPage, setShouldShowNoWorkspaceAccessPage] =
    useState<boolean>(false);

  const [
    hasFetchedCompaniesAndWorkspaces,
    setHasFetchedCompaniesAndWorkspaces,
  ] = useState<boolean>(false);

  /**
   * Stores the workspace ID that the user tried to access but was denied.
   * Triggers the effect to handle the redirect based on how many workspaces
   * the user has access to.
   */
  const [deniedWorkspaceId, setDeniedWorkspaceId] = useState<string | null>(
    null
  );

  const { fetchSelectedCompanyContext } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );

  const { handleErrorWithPage } = useErrorContext();

  // Fetch all companies and workspaces only once on page load
  useEffect(() => {
    async function fetchCompaniesAndWorkspacesViaStore(): Promise<void> {
      // Error is handled in the store slice
      await dispatch(fetchCompaniesAndWorkspaces()).unwrap();
      setHasFetchedCompaniesAndWorkspaces(true);
    }

    void fetchCompaniesAndWorkspacesViaStore();
  }, [dispatch]);

  // Handles loading spinner when fetching companies and workspaces
  useEffect(() => {
    // stop the spinner in case it is not being referenced from data-management.tsx file
    // or an error is raised during handleCompanyNavigation
    if (
      isLoadingSpinnerShowing &&
      (!globalThis.location.pathname.includes("data-management") ||
        shouldShowSelectWorkspaceMenu)
    ) {
      setLoadingSpinner(false);
    }
  }, [
    isLoadingSpinnerShowing,
    setLoadingSpinner,
    shouldShowSelectWorkspaceMenu,
  ]);

  // Fetches company features once on page load when user company is already defined
  useEffect(() => {
    async function fetchCompanyContextViaStore(): Promise<void> {
      await dispatch(fetchCompanyContext());
    }

    async function fetchCompanyCommunicationSettingsViaStore(): Promise<void> {
      await dispatch(fetchCompanyCommunicationSettings());
    }

    function fetchSubscriptionViaStore(): void {
      if (!canViewSubscriptionDetails) {
        return;
      }
      /**
       * Fetch company subscriptions with status active, expired and invalid. subscriptions can have the following statuses:
       * Active - between start and end date
       * Expired - end date is in the past
       * Invalid - between start and end date but at least one constraint is over the limit and
       * thus the subscription is not providing the Features it contains.
       */
      const queryOptions: FaroCoreApiTypes.CompanySubscriptionOptions = {
        status: ["ACTIVE", "EXPIRED", "INVALID"],
      };
      void dispatch(
        fetchCompanySubscriptions({
          queryOptions,
        })
      );
    }

    if (selectedSdbCompanyId) {
      void fetchCompanyContextViaStore();

      void fetchCompanyCommunicationSettingsViaStore();

      fetchSubscriptionViaStore();
    }
  }, [canViewSubscriptionDetails, dispatch, selectedSdbCompanyId]);


  /**
   * Looks for the first company the user has access to and navigates to it.
   */
  const navigateToFirstCompany = useCallback(
    (firstCompany: SdbCompany) => {
      if (
        firstCompany &&
        !isLocalhost() &&
        // If the company url is from the old dashboard
        firstCompany.url.includes(runtimeConfig.urls.hbDashboardUrl)
      ) {
        // Makes sure to redirect to the url that it is set on the backend
        // based on the subscriptions for that workspace.
        // This replace of href is only used to navigate to the old dashboard.
        // For the new dashboard, the navigation is handled by the router.
        window.location.href = firstCompany.url;
      } else {
        navigateToProjects({ companyId: firstCompany.id });
      }
    },
    [navigateToProjects]
  );

  /**
   * Handles the navigation to a company
   * @param companyIdFromParams - The company id to navigate to
   * @returns void
   */
  const handleCompanyNavigation = useCallback(
    async (companyIdFromParams: string): Promise<void> => {
      // Skip if we already showed toast for this workspace
      if (lastToastShownForWorkspaceId.current === companyIdFromParams) {
        return;
      }

      // Wait for workspaces to be fetched before validating access
      if (!hasFetchedCompaniesAndWorkspaces) {
        return;
      }

      // Check if user has access to this workspace
      const hasAccessToWorkspace = sdbCompanies.some(
        (company) => company.id === companyIdFromParams
      );

      if (!hasAccessToWorkspace) {
        // User doesn't have access - trigger denied workspace handling
        setDeniedWorkspaceId(companyIdFromParams);
        return;
      }

      try {
        if (companyIdFromParams !== selectedSdbCompanyId) {
          // Attempt to fetch the company context by the companyIdFromParams
          await getCompanyContext(companyIdFromParams, isDevModeEnabled);
          // Set the new selected company
          dispatch(setSelectedSdbCompanyId(companyIdFromParams));
        }
        // Hide selector workspace page
        setShouldShowSelectWorkspaceMenu(false);
        setDeniedWorkspaceId(null);
        lastToastShownForWorkspaceId.current = null;

      } catch (error) {
        // Core API returns an error with status 403 if the user does not have access to the resource
        // or 404 if the resource doesn't exist. In both cases store the denied workspace ID
        // and let the effect handle the redirect based on how many workspaces the user has.
        const errorStatus = HttpUtils.getErrorStatus(error);
        if (
          errorStatus &&
          (errorStatus === HttpUtils.HTTP_ERROR_CODE.NotFound ||
            errorStatus === HttpUtils.HTTP_ERROR_CODE.Forbidden)
        ) {
          setDeniedWorkspaceId(companyIdFromParams);
        } else {
          handleErrorWithPage({
            id: `getCompanyContext-${Date.now().toString()}`,
            title:
              "Failed to fetch the company. Please reload the page to try again.",
            error,
          });
        }
      }
    },
    [
      dispatch,
      hasFetchedCompaniesAndWorkspaces,
      isDevModeEnabled,
      sdbCompanies,
      selectedSdbCompanyId,
      handleErrorWithPage,
    ]
  );

  /**
   * Redirects user based on available workspaces:
   * - Single workspace: navigates directly to it
   * - Multiple workspaces: shows workspace selector
   */
  const redirectToAvailableWorkspace = useCallback(() => {
    sdbCompanies.length === 1
      ? navigateToFirstCompany(sdbCompanies[0])
      : setShouldShowSelectWorkspaceMenu(true);
  }, [navigateToFirstCompany, sdbCompanies]);

  // If there is a company ID in the URL params, navigate to it
  useEffect(() => {
    if (appParams.companyId && hasFetchedCompaniesAndWorkspaces) {
      void handleCompanyNavigation(appParams.companyId);
    }
  }, [appParams.companyId, hasFetchedCompaniesAndWorkspaces, handleCompanyNavigation]);

  // Handle workspace access denied after workspaces are fetched
  useEffect(() => {
    if (!deniedWorkspaceId || !hasFetchedCompaniesAndWorkspaces) {
      return;
    }

    if (lastToastShownForWorkspaceId.current === deniedWorkspaceId) {
      return;
    }

    const workspaceId = deniedWorkspaceId;
    // Mark as shown before clearing state to prevent duplicates
    lastToastShownForWorkspaceId.current = workspaceId;
    setDeniedWorkspaceId(null);

    if (sdbCompanies.length === 0) {
      // No workspaces - show forbidden page
      setShouldShowNoWorkspaceAccessPage(true);
      return;
    }

    // Show access denied toast for users with workspaces
    const redirectMessage =
      sdbCompanies.length === 1
        ? "You have been redirected to your workspace."
        : "Please select a different workspace or contact the workspace administrator.";

    showToast({
      message: "Access denied",
      description: (
        <>
          You don't have access to workspace <var>{workspaceId}</var>.{" "}
          {redirectMessage}
        </>
      ),
      type: "error",
    });

    redirectToAvailableWorkspace();
  }, [
    sdbCompanies.length,
    deniedWorkspaceId,
    hasFetchedCompaniesAndWorkspaces,
    redirectToAvailableWorkspace,
    showToast,
  ]);

  // If there isn't a company ID in the URL params but the user has fetched companies and workspaces
  // Then navigate the user to the only company or the workspace selection menu if they have multiple or zero.
  useEffect(() => {
    if (!appParams.companyId && hasFetchedCompaniesAndWorkspaces) {
      redirectToAvailableWorkspace();
    }
  }, [
    appParams.companyId,
    hasFetchedCompaniesAndWorkspaces,
    redirectToAvailableWorkspace,
  ]);

  // React to projectId url param changes and keep the projectId in the store.
  useEffect(() => {
    dispatch(setSelectedProjectId(appParams.projectId ?? null));
  }, [appParams.projectId, dispatch]);

  // Track subscription expired event
  useEffect(() => {
    // Prevent tracking multiple times if already tracked or you don't have features info
    if (hasTrackedSubscriptionExpiredEvent.current) {
      return;
    }

    // If fetching fails, the user subscription is unclear and we will navigate to expired page
    const hasFetchFailed = fetchSelectedCompanyContext === "rejected";

    // If fetching succeeds but core-product feature is not found, we will navigate to expired page
    const hasFetchSucceededButCoreProductDisabled =
      fetchSelectedCompanyContext === "succeeded" &&
      !isCoreProductFeatureEnabled;

    if (
      (hasFetchFailed || hasFetchSucceededButCoreProductDisabled) &&
      selectedSdbCompanyId
    ) {
      hasTrackedSubscriptionExpiredEvent.current = true;
      trackEvent({
        name: SubscriptionExpiredEvents.subscriptionExpired,
        props: {
          availableFeatures: availableFeaturesInCompany ?? [],
          fetchSelectedCompanyContext,
        },
      });
      // TODO: Remove beta testing: https://faro01.atlassian.net/browse/ST-3626
      if (isBetaTestingEnabled) {
        navigateToSubscriptionExpiredPage({ companyId: selectedSdbCompanyId });
      }
    }
    // We only want to track when subscription status actually changes
  }, [
    availableFeaturesInCompany,
    fetchSelectedCompanyContext,
    isBetaTestingEnabled,
    isCoreProductFeatureEnabled,
    navigateToSubscriptionExpiredPage,
    selectedSdbCompanyId,
    trackEvent,
  ]);

  return (
    <>
      {shouldShowNoWorkspaceAccessPage && (
        <AuthenticatedRoute>
          <ForbiddenAccessPage />
        </AuthenticatedRoute>
      )}
      {shouldShowSelectWorkspaceMenu && (
        <AuthenticatedRoute>
          <SelectWorkspacePage />
        </AuthenticatedRoute>
      )}
      {selectedSdbCompanyId && children}
    </>
  );
}
